import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, StatusBadge, formatMoney, formatDate } from '../regureStyles';
import { productLabel } from '../../utils/productCatalog';

const MyPoliciesScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const userLogin = useSelector(state => state.userLogin);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const user = userLogin?.userInfo?.data?.data || {};
  const role = user?.role || 'policyholder';
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Arriving from a My Clients row narrows the list to that client. The
  // key is an opaque handle, not an email — see portalClientKey.
  const clientKey = new URLSearchParams(location.search).get('client') || '';

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;

    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-policies`, {
          params: clientKey ? { client: clientKey } : {},
          withCredentials: true,
        });
        setPolicies(response.data?.data || []);
      } catch (err) {
        setError(err.response?.data?.message || t('screens.portal.failedLoadPolicies'));
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, [userLogin, apiUrl, clientKey]);

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  const pageTitle = t('screens.portal.yourPolicies');
  const pageSubtitle = role === 'broker' ? t('screens.portal.yourPoliciesSubtitleBroker') : t('screens.portal.yourPoliciesSubtitlePolicyholder');
  const tableTitle = role === 'broker' ? t('screens.portal.yourPolicies') : t('screens.portal.activePolicies');

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%'
      }}>
        {/* Page Header */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            <span style={{ color: S.burgundy, fontWeight: 700 }}>{t('screens.portal.breadcrumbPortal')}</span> / {t('screens.portal.breadcrumbMyPolicies')}
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
              {pageTitle}
            </div>
            <div style={{ fontSize: 14, color: S.textMuted, marginTop: 6 }}>
              {pageSubtitle}
            </div>
          </div>
        </div>

        {/* Table Card */}
        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)'
          }}>
            {error}
          </div>
        )}

        <div style={{
          background: '#fff',
          border: `1px solid ${S.borderLight}`,
          borderRadius: 14,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
        }}>
          <div style={{
            padding: '18px 24px',
            borderBottom: `1px solid ${S.borderLight}`,
            background: S.bg
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>
              {tableTitle}
            </div>
          </div>

          {policies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '80px 40px', color: S.textMuted }}>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{t('screens.portal.noPolicies')}</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {[
                    t('screens.portal.policiesColPolicyNumber'),
                    t('screens.portal.policiesColClient'),
                    t('screens.portal.policiesColProduct'),
                    t('screens.portal.policiesColStatus'),
                    t('screens.portal.policiesColPremium'),
                    t('screens.portal.policiesColPeriod'),
                    t('screens.portal.policiesColBroker'),
                  ].map(h => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '14px 24px',
                        fontSize: 11,
                        fontWeight: 700,
                        color: S.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        borderBottom: `1px solid ${S.borderLight}`,
                        background: S.bg,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {policies.map(policy => {
                  // The same canonical resolution the detail screen and the
                  // internal register already use: the authoritative
                  // `product` wins, and the lineOfBusiness family is only a
                  // fallback for a record that has no product at all.
                  // Reading the family directly put a stale classification
                  // under a column headed PRODUCT — travel-assistance
                  // policies read "Legal Protection" here while their own
                  // detail screen named them correctly.
                  const product = productLabel(t, policy.product, policy.lineOfBusiness, 'short');
                  const premium = policy.annualPremium ? formatMoney(policy.annualPremium, policy.currency) : '—';
                  const period = `${formatDate(policy.startDate)} – ${formatDate(policy.endDate)}`;
                  // Whose policy this is. A broker's list showed a column of
                  // policy numbers with no indication of the client behind
                  // any of them.
                  const clientName = policy.policyholder?.name
                    || policy.policyholder?.company
                    || '—';

                  return (
                    <tr
                      key={policy._id}
                      style={{
                        borderBottom: `1px solid ${S.borderLight}`,
                        cursor: 'pointer',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = S.bg}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      onClick={() => history.push(`/portal/my-policies/${policy._id}`)}
                    >
                      <td style={{ padding: '14px 24px', fontSize: 13, color: S.burgundy, fontWeight: 700 }}>
                        {policy.policyNumber}
                      </td>
                      <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                        {clientName}
                      </td>
                      <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                        {product}
                      </td>
                      <td style={{ padding: '14px 24px', fontSize: 13 }}>
                        <StatusBadge status={policy.status || 'active'} />
                      </td>
                      <td style={{ padding: '14px 24px', fontSize: 13, color: S.textPrimary, fontWeight: 600 }}>
                        {premium}
                      </td>
                      <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                        {period}
                      </td>
                      <td style={{ padding: '14px 24px', fontSize: 13, color: S.textSecondary }}>
                        {policy.brokerName || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default MyPoliciesScreen;
